import React from 'react'
import styled, {css, keyframes} from 'styled-components'
import {Calendar} from 'lucide-react'
import {Tooltip} from '../BaseComponents'
import {textOfChildren} from '../textTruncation'
import {CardTextLine} from './CardTextLine'
import {EllipsisText} from './Table'

/**
 * DonutCard
 *
 * Card with a cyan progress ring, a numeric center label, and optional
 * metadata rows beside the ring. Used wherever a fraction or
 * progress value is the primary read (Job Orders fill ratio, batch
 * completion, candidate progress, etc).
 *
 * The ring is rendered as inline SVG — no recharts dependency. The
 * caller passes `value` and `max`; the component computes the
 * stroke-dasharray. Tone selects from the --viz-* scale (default cyan).
 *
 * @example basic
 *   <DonutCard value={3} max={12} centerLabel="Filled" />
 *
 * @example with stats and a side header
 *   <DonutCard
 *     id="JO-3333"
 *     date="Oct 06"
 *     title="Packer"
 *     subtitle="Shift 2 · 2:00pm"
 *     meta={[{ label: 'Client', value: 'Coca-Cola' }]}
 *     value={3}
 *     max={12}
 *     centerLabel="Filled"
 *     stats={[
 *       { label: 'Requested', value: '1D : 8h' },
 *       { label: 'Fill rate', value: '25%' },
 *     ]}
 *     onClick={() => navigate(`/orders/${id}`)}
 *   />
 *
 * @example standalone donut (no card)
 *   <DonutCard variant="bare" value={42} max={100} centerLabel="Done" />
 */

export type DonutTone =
  | 'cyan'
  | 'cyan-soft'
  | 'blue'
  | 'blue-soft'
  | 'navy'
  | 'navy-soft'
  | 'warning'
  | 'error'
  | 'neutral'
export type DonutCardVariant = 'card' | 'bare'
export type DonutCardSize = 'sm' | 'card' | 'md' | 'lg'

/** Status tone for the optional notice pill (maps onto the DS status scale). */
export type DonutNoticeTone = 'error' | 'warning' | 'info' | 'success' | 'pending' | 'neutral'

export interface DonutCardMeta {
  label: string
  value: React.ReactNode
}

export interface DonutCardStat {
  label: string
  value: React.ReactNode
}

/**
 * The optional tag above the title (STFFLG-4184): a neutral pill saying how the viewer stands on
 * the card's subject, e.g. "Helping JobSource Chino". Separate from `notice`, the status pill
 * (urgency, outcome), so a card can carry both.
 */
export interface DonutCardTag {
  /** The words. A long label ends in "…", and only then shows the whole label in a tooltip. */
  label: string
  /** A small leading icon (decorative: the label carries the meaning). */
  icon?: React.ReactNode
  'data-testid'?: string
}

/** The notice line above the title: every order card shows one). */
export interface DonutCardNotice {
  tone: DonutNoticeTone
  label: React.ReactNode
  hideDot?: boolean
  ariaLabel?: string
  /** A live countdown: the dot pulses (1.6s), and stays still when the user asks for reduced motion. */
  pulse?: boolean
  /** A leading icon in place of the dot (pair it with `hideDot`), e.g. a started shift's lock. */
  icon?: React.ReactNode
}

export interface DonutCardProps {
  /** Current value (numerator) */
  value: number
  /** Upper bound (denominator). Ring fills proportionally; clamped 0-100%. */
  max: number

  /** Ring color from --viz-* scale. Default cyan. */
  tone?: DonutTone
  /** Visual size: sm 64px, card 68px (order cards, STFFLG-4333), md 88px (default), lg 140px */
  size?: DonutCardSize

  /** Text under the numeric center value (uppercase eyebrow) */
  centerLabel?: string
  /** Override the center text (default: "value / max") */
  centerOverride?: React.ReactNode

  /** Card header - left chip (e.g. "JO-3333") */
  id?: string
  /** Card header - right date pill */
  date?: string
  /** Optional status flags rendered beside the id chip. */
  headerFlags?: React.ReactNode
  /** Optional action rendered beside the date (for example a kebab menu). */
  headerAction?: React.ReactNode

  /** Body - large role/title (e.g. "Packer") */
  title?: string
  /** Body - subtitle (e.g. `<><strong>Shift:</strong> 2, 2:00pm</>`) */
  subtitle?: React.ReactNode

  /**
   * Opt-in tag above the title and notice (STFFLG-4184), e.g. "Helping {host branch}". Unset, the
   * card renders exactly as before.
   */
  tag?: DonutCardTag
  /** Optional status pill rendered above the title (e.g. urgency / outcome). */
  notice?: DonutCardNotice
  /** Optional left-accent frame color (urgency emphasis). */
  accent?: 'error' | 'warning' | 'neutral'

  /** Body - small meta rows below the title (e.g. Client: Coca-Cola) */
  meta?: DonutCardMeta[]
  /** Stats column rendered to the right of the donut (e.g. Requested / Fill rate) */
  stats?: DonutCardStat[]

  /** Render as a card shell (radius 16, shadow strong) or just the donut */
  variant?: DonutCardVariant
  /** Make the card clickable (cursor + onClick handler) */
  onClick?: () => void
  /** Cyan border + hover lift when "selected" by parent */
  selected?: boolean
  /**
   * Opt-in emphasis (STFFLG-4184), as ServiceOrderCard's: `outlined` draws a `--brand-cta` border
   * all round, the border a selected card draws, so a card that is unlike its neighbours (an order
   * the viewer helps on from another branch) stands out among them. Unset, the card renders exactly
   * as before. An `accent` still owns the left edge.
   */
  emphasis?: 'outlined'
  /** Extra CSS class forwarded to the root Card element (e.g. border-radius override for combo-mode stacking) */
  className?: string
  /**
   * Optional footer rendered below the donut row. Receives a flex
   * container with `justify-content: space-between` — pass left
   * content (e.g. presence avatars) and right content (e.g. action
   * buttons) as children.
   */
  footer?: React.ReactNode
}

/* ====================================================================
   TOKENS
   ==================================================================== */
const TONE_VARS: Record<DonutTone, string> = {
  cyan: 'var(--viz-cyan)',
  'cyan-soft': 'var(--viz-cyan-soft)',
  blue: 'var(--viz-blue)',
  'blue-soft': 'var(--viz-blue-soft)',
  navy: 'var(--viz-navy)',
  'navy-soft': 'var(--viz-navy-soft)',
  warning: 'var(--warning-500)',
  error: 'var(--error-500)',
  neutral: 'var(--neutral-500)',
}

const SIZE_PX: Record<
  DonutCardSize,
  {box: number; stroke: number; centerSize: string; labelSize: string}
> = {
  sm: {box: 64, stroke: 7, centerSize: '0.875rem', labelSize: '0.5625rem'},
  card: {box: 68, stroke: 7, centerSize: '0.9375rem', labelSize: '0.625rem'},
  md: {box: 88, stroke: 9, centerSize: '1.0625rem', labelSize: '0.5625rem'},
  lg: {box: 140, stroke: 14, centerSize: '1.5rem', labelSize: '0.625rem'},
}

const NOTICE_TONES: Record<DonutNoticeTone, {bg: string; fg: string; dot: string}> = {
  error: {bg: 'var(--error-100)', fg: 'var(--error-800)', dot: 'var(--error-500)'},
  warning: {bg: 'var(--warning-100)', fg: 'var(--warning-800)', dot: 'var(--warning-500)'},
  info: {bg: 'var(--info-100)', fg: 'var(--info-800)', dot: 'var(--info-500)'},
  success: {bg: 'var(--success-100)', fg: 'var(--success-800)', dot: 'var(--success-500)'},
  pending: {bg: 'var(--pending-100)', fg: 'var(--pending-800)', dot: 'var(--pending-500)'},
  neutral: {bg: 'var(--neutral-100)', fg: 'var(--primary-800)', dot: 'var(--primary-700)'},
}

const ACCENT_COLOR: Record<'error' | 'warning' | 'neutral', string> = {
  error: 'var(--error-500)',
  warning: 'var(--warning-500)',
  neutral: 'var(--neutral-500)',
}

/* ====================================================================
   STYLED
   ==================================================================== */
const Card = styled.article<{
  $clickable: boolean
  $selected: boolean
  $outlined: boolean
  $variant: DonutCardVariant
  $accent?: 'error' | 'warning' | 'neutral'
}>`
  ${({$variant}) =>
    $variant === 'card' &&
    `
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-strong);
    padding: var(--space-3) var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  `}

  transition: box-shadow 0.2s, transform 0.2s, border-color 0.15s;
  cursor: ${({$clickable}) => ($clickable ? 'pointer' : 'default')};
  /* Card variant carries the standard DS card frame line (same as KpiCard /
     DashboardCard / TabPanelWrapper) so it matches the cards it sits beside;
     selection swaps the hue, and the bare variant stays frameless. Width is
     always var(--border-thin) so selecting never shifts layout. */
  border: var(--border-thin) solid
    ${({$selected, $outlined, $variant}) =>
      $selected || ($outlined && $variant === 'card')
        ? 'var(--brand-cta)'
        : $variant === 'card'
        ? 'var(--border-subtle)'
        : 'transparent'};

  /* Must follow the border shorthand above, which would otherwise reset it. */
  ${({$accent, $variant}) =>
    $accent && $variant === 'card' && `border-left: 3px solid ${ACCENT_COLOR[$accent]};`}

  ${({$clickable, $variant}) =>
    $clickable &&
    $variant === 'card' &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-elevated);
    }
  `}

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

const Head = styled.div`
  min-height: var(--space-8);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
`

const HeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
`

const HeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
`

const IdChip = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  font-weight: 600;
  background: var(--neutral-100);
  color: var(--primary-800);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
`

const DateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 0.75rem;
  color: var(--neutral-700);
  font-weight: 500;
  font-family: var(--font-sans);
  white-space: nowrap;

  > i,
  > svg {
    color: var(--brand-active);
    font-size: 14px;
  }
`
export const CardKebabSlot = styled.span`
  display: inline-flex;
  margin-right: -0.625rem;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const livePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
`

const NoticePill = styled.div<{$tone: DonutNoticeTone; $hideDot?: boolean; $pulse?: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  align-self: flex-start;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  height: 1.375rem;
  padding: 0 var(--space-3);
  margin-bottom: 0.625rem;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: var(--lh-tight);
  background: ${({$tone}) => NOTICE_TONES[$tone].bg};
  color: ${({$tone}) => NOTICE_TONES[$tone].fg};
  white-space: nowrap;
  overflow: hidden;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({$tone}) => NOTICE_TONES[$tone].dot};
    ${({$hideDot}) => $hideDot && 'display: none;'}
    ${({$pulse}) =>
      $pulse &&
      css`
        animation: ${livePulse} 1.6s ease-in-out infinite;
      `}
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }

  svg {
    flex-shrink: 0;
  }

  > span {
    min-width: 0;
  }
`

/* Neutral, not a status: it says how the viewer stands on the order, and the notice below it keeps
   the status colours. The label's EllipsisText cuts a long branch name, and only then does its
   tooltip show the whole tag (STFFLG-4236). */
const TagPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  align-self: flex-start;
  max-width: 100%;
  min-width: 0;
  padding: var(--space-1) var(--space-3);
  margin-bottom: var(--space-1);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  /* The neutral notice's pairing (primary-800 on neutral-100), which passes AA at caption size. */
  background: var(--neutral-100);
  color: var(--primary-800);

  > svg {
    flex-shrink: 0;
  }

  /* The Tooltip wrapper around the label: it may shrink, so the label can be cut. */
  > span {
    min-width: 0;
  }
`

/* The EllipsisText inside cuts a long title with "…", and only then does its tooltip show the
   whole title (STFFLG-4236). */
const Title = styled.div`
  font-family: var(--font-sans);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-800);
  letter-spacing: -0.015em;
  line-height: 1.2;
  overflow: hidden;
`

/* Matches MetaBlock so a labelled subtitle ("Shift: 2, 11:30 PM") reads as the
   same kind of row as Client / Department directly below it. */
const Subtitle = styled.div`
  margin-top: var(--space-1);
  min-width: 0;
  overflow: hidden;
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-weight: 500;

  strong {
    color: var(--primary-800);
    font-weight: 600;
  }
`

const MetaBlock = styled.div`
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  min-width: 0;

  strong {
    color: var(--primary-800);
    font-weight: 600;
  }

  > div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

const Divider = styled.div`
  height: 1px;
  background: var(--divider);
  margin-bottom: var(--space-2);
`

const DonutRow = styled.div<{$hasStats: boolean}>`
  display: grid;
  grid-template-columns: auto ${({$hasStats}) => ($hasStats ? '1fr' : 'auto')};
  gap: var(--space-4);
  align-items: center;
`

const DonutWrap = styled.div<{$size: DonutCardSize}>`
  width: ${({$size}) => SIZE_PX[$size].box}px;
  height: ${({$size}) => SIZE_PX[$size].box}px;
  position: relative;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }
`

const DonutCenter = styled.div<{$size: DonutCardSize}>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .center-label {
    font-family: var(--font-sans);
    font-size: ${({$size}) => SIZE_PX[$size].labelSize};
    font-weight: 700;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: ${({$size}) => ($size === 'card' ? '0' : '2px')};
    line-height: ${({$size}) => ($size === 'card' ? '1' : 'inherit')};
  }

  .center-value {
    font-family: var(--font-mono);
    font-size: ${({$size}) => SIZE_PX[$size].centerSize};
    font-weight: 600;
    color: var(--primary-800);
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .center-value .total {
    color: var(--text-disabled);
  }
`

const StatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`

const StatRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 0.625rem;
  line-height: var(--lh-tight);
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const StatValue = styled.div`
  font-family: var(--font-mono);
  font-size: 0.9375rem;
  line-height: var(--lh-tight);
  font-weight: 600;
  color: var(--primary-800);
  font-variant-numeric: tabular-nums;
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
  padding-top: var(--space-2);
  /* When the card is stretched to match taller siblings in its row, the spare
     space lands above the footer so the actions stay pinned to the bottom edge
     and line up across the row. No effect on an auto-height card. */
  margin-top: auto;
`

/* ====================================================================
   UTILS
   ==================================================================== */
function clampPct(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0
  const pct = (value / max) * 100
  if (pct < 0) return 0
  if (pct > 100) return 100
  return pct
}

export interface DonutCenterFit {
  fontSize: string
  letterSpacing: string
  /** The eyebrow ("Filled") shows above the number. */
  showLabel: boolean
}

/**
 * The card donut's number by its length ("4/8" is 3 characters): 15px up to 5, 14px for 6, 12px for
 * 7, 11px for 8 or more. The 68px ring's hole is about 49px across, so from 6 characters on the
 * number takes the whole hole (no eyebrow, digits a little closer) and even "122/100" stays off the
 * ring. The donut's accessible name still says "Filled".
 */
export function donutCenterFit(value: number, max: number): DonutCenterFit {
  const chars = `${value}/${max}`.length
  if (chars <= 5) return {fontSize: '0.9375rem', letterSpacing: '-0.02em', showLabel: true}
  const fontSize = chars === 6 ? '0.875rem' : chars === 7 ? '0.75rem' : '0.6875rem'
  return {fontSize, letterSpacing: '-0.06em', showLabel: false}
}

/* ====================================================================
   COMPONENT
   ==================================================================== */
export const DonutCard: React.FC<DonutCardProps> = ({
  value,
  max,
  tone = 'cyan',
  size = 'md',
  centerLabel,
  centerOverride,
  id,
  date,
  headerFlags,
  headerAction,
  title,
  subtitle,
  tag,
  notice,
  accent,
  meta,
  stats,
  variant = 'card',
  onClick,
  selected = false,
  emphasis,
  className,
  footer,
}) => {
  const pct = clampPct(value, max)
  // Ring math: circumference @ r=40 = 251.32
  const RADIUS = 40
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS
  const dashLength = (pct / 100) * CIRCUMFERENCE
  const sizeTokens = SIZE_PX[size]
  const fit =
    size === 'card' && centerOverride === undefined ? donutCenterFit(value, max) : undefined

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    onClick?.()
  }

  const ariaLabel = centerLabel ?? title ?? 'Donut chart'

  const donutSvg = (
    <DonutWrap $size={size}>
      <svg viewBox="0 0 100 100" role="img" aria-label={`${value} of ${max} ${ariaLabel}`}>
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke="var(--viz-track)"
          strokeWidth={sizeTokens.stroke}
        />
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke={TONE_VARS[tone]}
          strokeWidth={sizeTokens.stroke}
          strokeLinecap="round"
          strokeDasharray={`${dashLength} ${CIRCUMFERENCE}`}
          style={{transition: 'stroke-dasharray 0.4s ease'}}
        />
      </svg>
      <DonutCenter $size={size}>
        {centerLabel && (fit?.showLabel ?? true) && (
          <span className="center-label">{centerLabel}</span>
        )}
        <span
          className="center-value"
          style={fit ? {fontSize: fit.fontSize, letterSpacing: fit.letterSpacing} : undefined}>
          {centerOverride ?? (
            <>
              {value}
              <span className="total">/{max}</span>
            </>
          )}
        </span>
      </DonutCenter>
    </DonutWrap>
  )

  if (variant === 'bare') {
    return donutSvg
  }

  const hasHeader = Boolean(id || date || headerFlags || headerAction)
  const hasNotice = Boolean(notice || tag)
  const hasTitleBlock = Boolean(title || subtitle)
  const hasMeta = Boolean(meta && meta.length > 0)
  const hasStats = Boolean(stats && stats.length > 0)

  return (
    <Card
      className={className}
      $clickable={Boolean(onClick)}
      $selected={selected}
      $outlined={emphasis === 'outlined'}
      $variant={variant}
      $accent={accent}
      onClick={onClick ? handleClick : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}>
      {hasHeader && (
        <Head>
          <HeaderLeft>
            {id && <IdChip>{id}</IdChip>}
            {headerFlags}
          </HeaderLeft>
          <HeaderRight>
            {date && (
              <DateChip>
                <Calendar size={14} aria-hidden="true" />
                {date}
              </DateChip>
            )}
            {headerAction && <CardKebabSlot>{headerAction}</CardKebabSlot>}
          </HeaderRight>
        </Head>
      )}

      {(hasNotice || hasTitleBlock || hasMeta) && (
        <TitleBlock>
          {tag && (
            <TagPill data-testid={tag['data-testid']}>
              {tag.icon}
              <Tooltip content={tag.label} block>
                <EllipsisText>{tag.label}</EllipsisText>
              </Tooltip>
            </TagPill>
          )}
          {notice && (
            <NoticePill
              $tone={notice.tone}
              $hideDot={notice.hideDot}
              $pulse={notice.pulse}
              aria-label={notice.ariaLabel}
              data-testid="donut-notice">
              {notice.icon}
              <Tooltip content={textOfChildren(notice.label)} block>
                <EllipsisText>{notice.label}</EllipsisText>
              </Tooltip>
            </NoticePill>
          )}
          {title && (
            <Title>
              <Tooltip content={title} block>
                <EllipsisText>{title}</EllipsisText>
              </Tooltip>
            </Title>
          )}
          {subtitle && (
            <Subtitle>
              <CardTextLine>{subtitle}</CardTextLine>
            </Subtitle>
          )}
          {hasMeta && (
            <MetaBlock>
              {meta!.map((m, i) => (
                <div key={`${m.label}-${i}`}>
                  <CardTextLine>
                    <strong>{m.label}:</strong> {m.value}
                  </CardTextLine>
                </div>
              ))}
            </MetaBlock>
          )}
        </TitleBlock>
      )}

      {(hasHeader || hasNotice || hasTitleBlock || hasMeta) && <Divider />}

      <DonutRow $hasStats={hasStats}>
        {donutSvg}
        {hasStats && (
          <StatList>
            {stats!.map((s, i) => (
              <StatRow key={`${s.label}-${i}`}>
                <StatLabel>{s.label}</StatLabel>
                <StatValue>{s.value}</StatValue>
              </StatRow>
            ))}
          </StatList>
        )}
      </DonutRow>

      {footer && <CardFooter>{footer}</CardFooter>}
    </Card>
  )
}

DonutCard.displayName = 'DonutCard'
